import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as turf from '@turf/turf';
import {
  Select,
  Spin,
  Switch,
  Tag,
  Typography,
  Drawer,
  Button,
  Statistic,
  message,
  Tabs,
  Divider,
  Tooltip,
  Alert,
} from 'antd';
import debounce from 'lodash.debounce';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CloseOutlined,
  EditOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

import NafPicker from '../Filters/NafPicker';
import { useAuth } from '../../AuthContext';
import { buildActivitySummary } from '../../utils/activitySummary';

import CreatePlaylistDrawer from './CreatePlaylistDrawer';
import SaveProductDrawer from './SaveProductDrawer';

const { Text } = Typography;

function getDrawerWidth() {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  if (vw < 992) return 460;
  if (vw < 1280) return 540;
  return 600;
}
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const HEADER_H = 130;

const BASE_WIDTH = 460;
const ACTIVITY_WIDTH = 900;
const clampWidth = w =>
  typeof window === 'undefined' ? w : Math.min(w, window.innerWidth - 80);
const REGION_FILL = '#205D9E',
  DEPT_FILL = '#104D87',
  COMM_HOVER_FILL = '#004074',
  BORDER_BLUE = '#0D2847',
  SELECT_FILL = '#E54D2E',
  BTN_BG = '#FBD3CB',
  BTN_COLOR = '#000',
  DEPT_LINE_WIDTH = 2;

// Contours servis depuis /public : la source etalab redirige vers un bucket OVH
// sans en-tete CORS, ce qui bloquait le chargement des zones de selection.
const GEO_BASE = `${process.env.PUBLIC_URL || ''}/geo`;
const REGIONS_GEO = `${GEO_BASE}/regions-1000m.geojson`;
const DEPTS_GEO = `${GEO_BASE}/departements-1000m.geojson`;

const id2dept = raw => {
  const id = String(raw);
  const special = ['971', '972', '973', '974', '976', '975', '977', '978'];
  return special.includes(id.slice(0, 3)) ? id.slice(0, 3) : id.slice(0, 2);
};
function extractPostalCodes(userComm) {
  const all = [];
  (userComm || []).forEach(c => {
    if (Array.isArray(c.codesPostaux) && c.codesPostaux.length) {
      all.push(...c.codesPostaux);
    } else if (c.cp) {
      all.push(c.cp);
    }
  });
  return Array.from(new Set(all.filter(x => /^\d{5}$/.test(String(x)))));
}

export default function CadastreDeptCommuneMap() {
  const PLAIN_POPUP_CSS = `
     .plain-popup .mapboxgl-popup-content {      
    background: transparent;
    padding: 0;
    box-shadow: none;
    border: 0;
  }
  .plain-popup .mapboxgl-popup-tip { display:none; } 
  .caddie-label{
    display:inline-block;
    padding:4px 10px;
    font-size:13px;font-weight:600;
    background:${BTN_BG};color:${BTN_COLOR};
    border:1px solid #333;border-radius:16px;
    white-space:nowrap;
    box-shadow:0 1px 2px rgba(0,0,0,.25);
  }
    .ant-drawer-content-wrapper {
  box-shadow: none !important;
}
  `;
  useEffect(() => {
    const tag = document.createElement('style');
    tag.innerHTML = PLAIN_POPUP_CSS;
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);

  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const autoLoadRef = useRef(false);

  const container = useRef(null),
    map = useRef(null),
    communesRef = useRef(null),
    popupRef = useRef(null);
  const deptMarkers = useRef({}),
    commMarkers = useRef({});
  const commBtn = useRef(null),
    activeComm = useRef(null);
  const userCommRef = useRef([]);
  const commGeoRef = useRef({}); // GeoJSON communes par dept : { [deptCode]: geoJSON }
  const commDeptFeaturesRef = useRef({}); // features dept boundary par dept : { [deptCode]: feature }

  const [loadingDrawer] = useState(false);
  const [drawerOpen] = useState(true);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [regionList, setRegionList] = useState([]),
    [deptList, setDeptList] = useState([]);
  const [regionsGeo, setRegionsGeo] = useState(null),
    [deptsGeo, setDeptsGeo] = useState(null);

  const [selRegion, setSelRegion] = useState(null),
    [selDept, setSelDept] = useState(null),
    [commMode, setCommMode] = useState(false),
    [, setLoading] = useState(false);

  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  // États pour les établissements sur la carte
  const [establishments, setEstablishments] = useState([]);
  const [establishmentsLoading, setEstablishmentsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [, setShowEstablishments] = useState(false);

  const [userDepts, setUserDepts] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selDepts')) || [];
    } catch {
      return [];
    }
  });

  const [userComm, setUserComm] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selComm')) || [];
    } catch {
      return [];
    }
  });
  const [emailOnly, setEmailOnly] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saveProductOpen, setSaveProductOpen] = useState(false);
  const [updateProduct, setUpdateProduct] = useState(null); // { slug, name, ...product } pour mode édition

  // Onglet Visualiser
  const [viewPlaylists, setViewPlaylists] = useState([]);
  const [viewProducts, setViewProducts] = useState([]);
  const [viewListsLoaded, setViewListsLoaded] = useState(false);
  const [viewSource, setViewSource] = useState(null); // { type: 'playlist'|'product', id }
  const [viewLoading, setViewLoading] = useState(false);
  const [viewPage, setViewPage] = useState(0); // 0 = nothing loaded yet
  const [viewAppend, setViewAppend] = useState(true); // true = ajouter, false = remplacer
  const [viewListOpen, setViewListOpen] = useState(false);
  const [webGLError, setWebGLError] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [viewTotals, setViewTotals] = useState({ totalAll: 0, totalGeo: 0 });
  const [selectedEst, setSelectedEst] = useState(null);
  const estPopupRef = useRef(null);
  const navigate = useNavigate();

  const [selNaf, setSelNaf] = useState([]);
  const [macroThemes, setMacroThemes] = useState([]);

  const [showCommLabels, setShowCommLabels] = useState(false);
  const [drawMode, setDrawMode] = useState(null); // null | 'circle' | 'polygon'
  const drawModeRef = useRef(null);
  const drawStateRef = useRef({ center: null, vertices: [] });
  const [drawVertexCount, setDrawVertexCount] = useState(0);
  const [themesLoading, setThemesLoading] = useState(true);
  const [themesErr, setThemesErr] = useState(null);
  const token = localStorage.getItem('prae.jwt');

  const loadViewLists = async (force = false) => {
    if (!force && viewListsLoaded) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [plRes, prRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/playlists`, {
          headers,
        }),
        fetch(`${process.env.REACT_APP_API_URL}/packaged-products/admin`, {
          headers,
        }),
      ]);
      setViewPlaylists((await plRes.json()) || []);
      setViewProducts((await prRes.json()) || []);
      setViewListsLoaded(true);
    } catch (e) {
      message.error('Erreur chargement des listes');
    }
  };

  const handleEditProduct = async () => {
    const product = viewProducts.find(p => p.slug === viewSource.id);
    if (!product) return;

    const inseeList = (product.filter_communes_insee || []).map(String);
    const cpList = (product.filter_communes || []).map(String);
    const depts = (product.filter_departments || []).map(String);
    const deptPrefixes =
      depts.length > 0
        ? depts
        : Array.from(new Set(inseeList.map(c => c.slice(0, 2))));

    // Charger les GeoJSON + cpMap pour enrichir les features avec codesPostaux
    if (deptPrefixes.length > 0) {
      await Promise.all(
        deptPrefixes.map(async dep => {
          const [geo, cpArr] = await Promise.all([
            commGeoRef.current[dep]
              ? Promise.resolve(commGeoRef.current[dep])
              : fetch(
                  `https://cadastre.data.gouv.fr/bundler/cadastre-etalab/departements/${dep}/geojson/communes`
                ).then(r => r.json()),
            fetch(
              `https://geo.api.gouv.fr/departements/${dep}/communes?fields=code,codesPostaux`
            ).then(r => r.json()),
          ]);
          const cpMap = Object.fromEntries(
            cpArr.map(o => [
              o.code,
              Array.isArray(o.codesPostaux) ? o.codesPostaux : [o.codesPostaux],
            ])
          );
          geo.features.forEach(f => {
            const code = String(f.properties.code || f.id);
            f.properties.code = code;
            f.properties.codesPostaux = cpMap[code] || [];
          });
          commGeoRef.current[dep] = geo;
        })
      );

      // Si des codes INSEE explicites existent, les utiliser tels quels (pas d'expansion CP)
      // Sinon, étendre depuis les codes postaux
      const cpSet = new Set(cpList);
      const extendedInseeSet = new Set(inseeList);
      if (inseeList.length === 0) {
        deptPrefixes.forEach(dep => {
          (commGeoRef.current[dep]?.features || []).forEach(f => {
            if (
              (f.properties.codesPostaux || []).some(cp =>
                cpSet.has(String(cp))
              )
            ) {
              extendedInseeSet.add(f.properties.code);
            }
          });
        });
      }

      // Reconstruire userComm
      const newComm = [];
      extendedInseeSet.forEach(insee => {
        const dep = insee.slice(0, 2);
        const feat = (commGeoRef.current[dep]?.features || []).find(
          f => f.properties.code === insee
        );
        if (!feat) return;
        const [lng, lat] = turf.centroid(feat).geometry.coordinates;
        if (isNaN(lng) || isNaN(lat)) return;
        const codesPostaux = feat.properties.codesPostaux || [];
        newComm.push({
          id: insee,
          nom: feat.properties.nom || insee,
          cp: codesPostaux[0] || '',
          codesPostaux,
          lng,
          lat,
        });
      });
      setUserComm(newComm);

      // Activer commMode sur le premier dept pour afficher la grille communes
      if (inseeList.length > 0 || cpList.length > 0) {
        setSelDept(deptPrefixes[0]);
        setCommMode(true);
      } else {
        setUserDepts(depts);
      }
    }

    // Restaurer NAF + emailOnly
    setSelNaf(product.filter_naf || []);
    setEmailOnly(product.filter_email_only || false);

    // Quitter viewMode
    const slug = viewSource.id;
    setViewMode(false);
    setViewSource(null);
    setEstablishments([]);
    map.current
      .getSource('viewZone')
      ?.setData({ type: 'FeatureCollection', features: [] });
    map.current
      .getSource('commSel')
      ?.setData({ type: 'FeatureCollection', features: [] });

    // Préparer le mode mise à jour (le drawer s'ouvrira quand l'utilisateur cliquera "Sauvegarder produit")
    setUpdateProduct({ ...product, slug });
  };

  const handleViewOnMap = async (page = 1) => {
    if (!viewSource) return;
    setViewLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Déterminer si c'est une playlist gestion
      const isGestion =
        viewSource.type === 'playlist' &&
        viewPlaylists.find(p => p.id === viewSource.id)?.source_type ===
          'gestion';

      let base;
      if (isGestion) {
        base = `${process.env.REACT_APP_API_URL}/playlists/${viewSource.id}/gestion-map`;
      } else if (viewSource.type === 'playlist') {
        base = `${process.env.REACT_APP_API_URL}/playlists/${viewSource.id}/map`;
      } else {
        base = `${process.env.REACT_APP_API_URL}/packaged-products/${viewSource.id}/establishments`;
      }
      const res = await fetch(`${base}?page=${page}&pageSize=300`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      // Support réponse tableau (public) ou objet { establishments, totalGeo, totalAll } (admin)
      const list = Array.isArray(json) ? json : json.establishments || [];
      if (page === 1 && !Array.isArray(json)) {
        setViewTotals({
          totalAll: json.totalAll || 0,
          totalGeo: json.totalGeo || 0,
        });
      }

      if (page === 1 || !viewAppend) {
        setEstablishments(list);
      } else {
        setEstablishments(prev => [...prev, ...list]);
      }
      setViewPage(page);
      setShowEstablishments(true);
      message.success(
        page === 1 || !viewAppend
          ? `${list.length} contacts chargés`
          : `+${list.length} contacts ajoutés`
      );
    } catch (e) {
      message.error(`Erreur : ${e.message}`);
    } finally {
      setViewLoading(false);
    }
  };

  const buildFiltersPayload = () => ({
    depts: userDepts,
    communesInsee: userComm.map(c => c.id),
    communes: extractPostalCodes(userComm),
    naf: selNaf,
    emailOnly,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/naf/macro-themes`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (alive)
          setMacroThemes(Array.isArray(json?.themes) ? json.themes : []);
      } catch (e) {
        if (alive) setThemesErr(e);
      } finally {
        if (alive) setThemesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onResize = () => setDrawerWidth(getDrawerWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [activeFilterTab, setActiveFilterTab] = useState('geo');
  const [drawerWidth, setDrawerWidth] = useState(() => clampWidth(BASE_WIDTH));
  useEffect(() => {
    const target = activeFilterTab === 'activity' ? ACTIVITY_WIDTH : BASE_WIDTH;
    setDrawerWidth(clampWidth(target));
  }, [activeFilterTab]);

  useEffect(() => {
    const onResize = () => {
      const target =
        activeFilterTab === 'activity' ? ACTIVITY_WIDTH : BASE_WIDTH;
      setDrawerWidth(clampWidth(target));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeFilterTab]);

  useEffect(() => {
    if (!map.current) return;
    const t = setTimeout(() => map.current?.resize(), 270);
    return () => clearTimeout(t);
  }, [drawerWidth]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `.ant-drawer-content-wrapper{transition:width .25s ease}`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const clickRef = useRef(null);
  const hoverMoveRef = useRef(null);
  const hoverLeaveRef = useRef(null);

  const refreshCount = async payload => {
    const hasAny =
      (payload.depts && payload.depts.length) ||
      (payload.communesInsee && payload.communesInsee.length) ||
      (payload.communes && payload.communes.length) ||
      (payload.naf && payload.naf.length);

    if (!hasAny) {
      setCount(null);
      return;
    }

    setCountLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/targets/count`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        }
      );
      const json = await res.json();
      setCount(json.count ?? 0);
    } catch (e) {
      console.error('Count API error', e);
      setCount(null);
    } finally {
      setCountLoading(false);
    }
  };

  const refreshCountDebounced = useCallback(
    debounce(() => refreshCount(buildFiltersPayload()), 300),
    [userDepts, userComm, selNaf, emailOnly] // dépendances
  );

  // Réagit aux changements de params URL (montage + navigation dans la même page)
  useEffect(() => {
    const viewParam = searchParams.get('view');
    const idParam = searchParams.get('id');

    if (!viewParam || !idParam) {
      // Params absents : quitter le mode visualisation
      setViewMode(false);
      setViewSource(null);
      setViewPage(0);
      setEstablishments([]);
      setViewListOpen(false);
      setViewTotals({ totalAll: 0, totalGeo: 0 });
      estPopupRef.current?.remove();
      return;
    }

    const type =
      viewParam === 'playlist'
        ? 'playlist'
        : viewParam === 'product'
          ? 'product'
          : null;
    if (!type) return;

    (async () => {
      // Vider les sélections du mode filtre pour repartir d'une carte propre
      setUserDepts([]);
      setSelDept(null);
      clearCommMarkers();
      setUserComm([]);
      commGeoRef.current = {};
      commDeptFeaturesRef.current = {};
      refreshCommDeptOutlines();
      if (map.current) {
        map.current
          .getSource?.('geo')
          ?.setData({ type: 'FeatureCollection', features: [] });
      }
      setSelNaf([]);
      setEstablishments([]);
      await loadViewLists();
      autoLoadRef.current = true;
      setCommMode(false);
      setViewMode(true);
      setViewSource({ type, id: idParam });
      setViewListOpen(true);
    })();
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-charger les établissements quand viewSource change depuis URL
  useEffect(() => {
    if (!autoLoadRef.current || !viewSource) return;
    autoLoadRef.current = false;
    handleViewOnMap(1);
  }, [viewSource]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les établissements pour la carte
  const loadEstablishments = async (page = 1) => {
    const hasAny =
      (userDepts && userDepts.length) ||
      (userComm && userComm.length) ||
      (selNaf && selNaf.length);

    if (!hasAny) {
      setEstablishments([]);
      return;
    }

    setEstablishmentsLoading(true);
    try {
      const payload = buildFiltersPayload();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/targets/map`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ...payload, page, pageSize: 300 }),
      });
      const json = await res.json();

      if (page === 1) {
        setEstablishments(json.establishments || []);
      } else {
        setEstablishments(prev => [...prev, ...(json.establishments || [])]);
      }
      setCurrentPage(page);
      if (page === 1) setViewListOpen(true);
    } catch (e) {
      console.error('Establishments API error', e);
      message.error('Erreur lors du chargement des établissements');
    } finally {
      setEstablishmentsLoading(false);
    }
  };

  useEffect(() => {
    refreshCountDebounced();
  }, [refreshCountDebounced]);

  // Réinitialiser la sélection quand la liste change
  useEffect(() => {
    setSelectedEst(null);
    estPopupRef.current?.remove();
  }, [establishments]);

  // Mettre à jour les markers des établissements sur la carte
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Convertir en GeoJSON
    const geojson = {
      type: 'FeatureCollection',
      features: establishments.map(est => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [est.longitude, est.latitude],
        },
        properties: {
          name: est.name,
          address: est.address,
          city: est.city,
          postalCode: est.postalCode,
          activity: est.activity,
        },
      })),
    };

    // Vérifier si la source existe déjà
    if (map.current.getSource('establishments')) {
      map.current.getSource('establishments').setData(geojson);
    } else {
      // Créer la source avec clustering
      map.current.addSource('establishments', {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Cluster circles
      map.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'establishments',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6',
            10,
            '#f1f075',
            30,
            '#f28cb1',
          ],
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40],
        },
      });

      // Cluster count
      map.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'establishments',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      });

      // Individual points
      map.current.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'establishments',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#11b4da',
          'circle-radius': 6,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
      });

      // Click sur un cluster pour zoomer
      map.current.on('click', 'clusters', e => {
        const features = map.current.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        });
        const clusterId = features[0].properties.cluster_id;
        map.current
          .getSource('establishments')
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            map.current.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom,
            });
          });
      });

      // Popup sur un point individuel
      map.current.on('click', 'unclustered-point', e => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const { name, address, city, postalCode, activity } =
          e.features[0].properties;

        const html = `
          <div style="font-family: sans-serif; min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${name}</h3>
            <p style="margin: 4px 0; font-size: 12px; color: #666;">
              ${address || ''}<br/>
              ${postalCode} ${city}
            </p>
            <p style="margin: 8px 0 0 0; padding-top: 8px; border-top: 1px solid #eee; font-size: 11px; color: #999;">
              <strong>Activité :</strong> ${activity || 'N/A'}
            </p>
          </div>
        `;

        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(html)
          .addTo(map.current);
      });

      // Curseur pointer sur hover
      map.current.on('mouseenter', 'clusters', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'clusters', () => {
        map.current.getCanvas().style.cursor = '';
      });
      map.current.on('mouseenter', 'unclustered-point', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'unclustered-point', () => {
        map.current.getCanvas().style.cursor = '';
      });
    }
  }, [establishments, mapLoaded]);

  useEffect(() => {
    localStorage.setItem('selDepts', JSON.stringify(userDepts));
  }, [userDepts]);

  useEffect(() => {
    localStorage.setItem('selComm', JSON.stringify(userComm));
  }, [userComm]);

  function firstCp(raw) {
    if (!raw) return '';

    if (Array.isArray(raw)) return raw[0] || '';

    if (typeof raw === 'string' && raw.trim().startsWith('[')) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length) return arr[0];
    }

    return raw;
  }

  const handleGeoClick = e => {
    if (drawModeRef.current) return; // draw mode intercepts clicks
    if (!communesRef.current) return;
    const f = e.features?.[0];
    if (!f) return;

    const id = String(f.properties.code);

    // Déjà sélectionnée → l'exclure
    if (userCommRef.current.some(o => o.id === id)) {
      removeFixedCommMarker(id);
      setUserComm(prev => prev.filter(o => o.id !== id));
      popupRef.current.remove();
      map.current.setFilter('comm-hover', ['==', ['get', 'code'], '']);
      return;
    }

    // Pas sélectionnée → l'ajouter
    const nom = f.properties.nom;
    const cp = firstCp(f.properties.codesPostaux);
    const [lng, lat] = turf.centroid(f).geometry.coordinates;
    const codesPostaux = f.properties.codesPostaux || (cp ? [cp] : []);
    setUserDepts(d => d.filter(c => c !== id2dept(id)));
    addFixedCommMarker({ id, nom, cp, codesPostaux, lng, lat });
    setUserComm(prev => [...prev, { id, nom, cp, codesPostaux, lng, lat }]);
    hideCommPopup();
  };

  useEffect(() => {
    setUserComm(prev =>
      prev.map(o => (o.cp ? o : { ...o, cp: firstCp(o.codesPostaux) }))
    );
  }, []);

  useEffect(() => {
    fetch('https://geo.api.gouv.fr/regions?fields=code,nom')
      .then(r => r.json())
      .then(setRegionList);
    fetch('https://geo.api.gouv.fr/departements?fields=code,nom')
      .then(r => r.json())
      .then(setDeptList);
  }, []);

  useEffect(() => {
    if (map.current) return;
    if (!mapboxgl.supported({ failIfMajorPerformanceCaveat: false })) {
      setWebGLError(true);
      return;
    }
    try {
      map.current = new mapboxgl.Map({
        container: container.current,
        style: 'mapbox://styles/devis-l/clr2bt9ro005d01o74ilq4pnj',
        center: [2.35, 48.85],
        zoom: 6,
        projection: 'mercator',
        antialias: true,
        failIfMajorPerformanceCaveat: false,
      });
    } catch (e) {
      setWebGLError(true);
      return;
    }
    map.current.on('load', () => {
      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -12],
        className: 'plain-popup',
      });
      map.current.addSource('geo', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.current.addSource('dept', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.current.addLayer({
        id: 'geo-fill',
        type: 'fill',
        source: 'geo',
        paint: { 'fill-color': REGION_FILL, 'fill-opacity': 0.3 },
      });
      map.current.addLayer({
        id: 'geo-line',
        type: 'line',
        source: 'geo',
        paint: { 'line-color': BORDER_BLUE, 'line-width': 1 },
      });
      map.current.addLayer({
        id: 'dept-line',
        type: 'line',
        source: 'dept',
        layout: { visibility: 'none' },
        paint: { 'line-color': BORDER_BLUE, 'line-width': DEPT_LINE_WIDTH },
      });
      map.current.addLayer({
        id: 'dept-hover',
        type: 'fill',
        source: 'dept',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': '#CCE5FF',
          'fill-opacity': 0.4,
        },
        filter: ['==', ['get', 'code'], ''],
      });

      map.current.addSource('deptSel', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.current.addLayer({
        id: 'dept-selected-line',
        type: 'line',
        source: 'deptSel',
        paint: {
          'line-color': BORDER_BLUE,
          'line-width': DEPT_LINE_WIDTH,
        },
      });
      // Source dédiée à la zone Visualiser (ne conflicte pas avec deptSel)
      map.current.addSource('viewZone', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.current.addLayer({
        id: 'view-zone-fill',
        type: 'fill',
        source: 'viewZone',
        paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.15 },
      });
      map.current.addLayer({
        id: 'view-zone-line',
        type: 'line',
        source: 'viewZone',
        paint: { 'line-color': '#1d4ed8', 'line-width': 2 },
      });

      map.current.addSource('commSel', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.current.addLayer({
        id: 'comm-selected-fill',
        type: 'fill',
        source: 'commSel',
        paint: {
          'fill-color': SELECT_FILL, // rouge
          'fill-opacity': 0.6,
        },
      });
      map.current.addLayer({
        id: 'comm-selected-line',
        type: 'line',
        source: 'commSel',
        paint: {
          'line-color': BORDER_BLUE,
          'line-width': 1,
        },
      });
      map.current.addLayer({
        id: 'dept-selected-fill',
        type: 'fill',
        source: 'deptSel',
        paint: {
          'fill-color': SELECT_FILL, // #E54D2E
          'fill-opacity': 0.6,
        },
      });
      map.current.addLayer({
        id: 'comm-selected',
        type: 'fill',
        source: 'geo',
        paint: { 'fill-color': SELECT_FILL, 'fill-opacity': 0.6 },
        filter: ['in', ['get', 'code'], ['literal', []]],
      });
      map.current.addLayer({
        id: 'comm-hover',
        type: 'fill',
        source: 'geo',
        paint: { 'fill-color': COMM_HOVER_FILL, 'fill-opacity': 0.5 },
        filter: ['==', ['get', 'code'], null],
        layout: { visibility: 'none' },
      });
      map.current.on('mousemove', 'dept-line', e => {
        const code = e.features[0]?.properties?.code || '';
        map.current.setFilter('dept-hover', ['==', ['get', 'code'], code]);
      });
      map.current.on('mouseleave', 'dept-line', () => {
        map.current.setFilter('dept-hover', ['==', ['get', 'code'], '']);
      });
      if (clickRef.current) {
        map.current.off('click', 'geo-fill', clickRef.current);
        map.current.off('click', 'comm-selected-fill', clickRef.current);
        clickRef.current = null;
      }

      // Contour orange des depts en mode communes
      map.current.addSource('commDeptOutlines', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.current.addLayer({
        id: 'comm-dept-outline',
        type: 'line',
        source: 'commDeptOutlines',
        paint: { 'line-color': '#c2410c', 'line-width': 3 },
      });

      // Source pour le dessin de zones
      map.current.addSource('drawPreview', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.current.addLayer({
        id: 'draw-preview-fill',
        type: 'fill',
        source: 'drawPreview',
        paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.15 },
      });
      map.current.addLayer({
        id: 'draw-preview-line',
        type: 'line',
        source: 'drawPreview',
        paint: {
          'line-color': '#1d4ed8',
          'line-width': 2,
          'line-dasharray': [4, 3],
        },
      });

      setMapLoaded(true);
    });
  }, []);

  useEffect(() => {
    userCommRef.current = userComm;
  }, [userComm]);
  useEffect(() => {
    if (map.current) map.current.resize();
  }, [drawerOpen]);
  useEffect(() => {
    if (!mapLoaded) return;

    if (commMode) {
      clearDeptMarkers();
    } else {
      userDepts.forEach(code => {
        if (!deptMarkers.current[code]) {
          const f = deptsGeo?.features?.find(x => x.properties.code === code);
          if (f) addDeptMarker(f);
        }
      });
    }
  }, [commMode, mapLoaded, userDepts, deptsGeo]);

  const clearDeptMarkers = () => {
    Object.values(deptMarkers.current).forEach(m => m.remove());
    deptMarkers.current = {};
  };

  const refreshCommDeptOutlines = () => {
    map.current?.getSource('commDeptOutlines')?.setData({
      type: 'FeatureCollection',
      features: Object.values(commDeptFeaturesRef.current),
    });
  };

  const clearCommMarkers = () => {
    Object.keys(commMarkers.current).forEach(id => removeFixedCommMarker(id));
  };

  useEffect(() => {
    Object.values(commMarkers.current).forEach(m => {
      m.getElement().style.display = showCommLabels ? '' : 'none';
    });
  }, [showCommLabels]);

  const refreshDeptStyle = code => {
    const mk = deptMarkers.current[code];
    if (!mk) return;
    const el = mk.getElement();
    const sel = userDepts.includes(code);
    el.style.background = sel ? BTN_BG : '#fff';
    el.style.color = sel ? BTN_COLOR : '#000';
  };
  const addDeptMarker = feat => {
    if (communesRef.current) return;
    const { code, nom } = feat.properties;
    if (deptMarkers.current[code]) deptMarkers.current[code].remove();
    const [lng, lat] = turf.centroid(feat).geometry.coordinates;
    const el = document.createElement('div');
    el.innerHTML = `🛒 ${nom} (${code})`;
    Object.assign(el.style, {
      borderRadius: '16px',
      padding: '4px 10px',
      fontSize: '13px',
      fontWeight: 600,
      background: '#FFF',
      border: '1px solid #333',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      boxShadow: '0 1px 2px rgba(0,0,0,.25)',
    });
    refreshDeptStyle(code);
    el.onclick = () =>
      setUserDepts(prev => {
        const add = !prev.includes(code);
        if (add) {
          setUserComm(c => c.filter(o => id2dept(o.id) !== code));
        }
        return add ? [...prev, code] : prev.filter(d => d !== code);
      });
    el.onclick = () => {
      setUserDepts(prev => {
        const add = !prev.includes(code);

        if (add) {
          setUserComm(cPrev => {
            cPrev
              .filter(o => id2dept(o.id) === code)
              .forEach(o => removeFixedCommMarker(o.id));
            return cPrev.filter(o => id2dept(o.id) !== code);
          });
        }

        return add ? [...prev, code] : prev.filter(d => d !== code);
      });
    };
    el.onmouseenter = () => {
      el.style.background = BTN_BG;
      el.style.color = BTN_COLOR;
    };
    el.onmouseleave = () => refreshDeptStyle(code);
    deptMarkers.current[code] = new mapboxgl.Marker({
      element: el,
      anchor: 'center',
    })
      .setLngLat([lng, lat])
      .addTo(map.current);
  };

  const addFixedCommMarker = ({ id, nom, lng, lat }) => {
    if (commMarkers.current[id]) return;
    const el = document.createElement('div');
    el.innerText = `🛒${nom}`;
    Object.assign(el.style, {
      borderRadius: '16px',
      padding: '4px 10px',
      fontSize: '13px',
      fontWeight: 600,
      background: BTN_BG,
      color: BTN_COLOR,
      border: '1px solid #333',
      whiteSpace: 'nowrap',
      boxShadow: '0 1px 2px rgba(0,0,0,.25)',
      cursor: 'pointer',
    });

    el.onclick = e => {
      e.stopPropagation();
      removeFixedCommMarker(id);
      setUserComm(prev => prev.filter(o => o.id !== id));
    };

    el.onmouseenter = () => {
      el.style.opacity = 0.8;
    };
    el.onmouseleave = () => {
      el.style.opacity = 1;
    };
    if (!showCommLabels) el.style.display = 'none';
    commMarkers.current[id] = new mapboxgl.Marker({
      element: el,
      anchor: 'center',
    })
      .setLngLat([lng, lat])
      .addTo(map.current);
  };
  const removeFixedCommMarker = id => {
    commMarkers.current[id]?.remove();
    delete commMarkers.current[id];
  };

  const clearCommBtn = () => {
    commBtn.current?.remove();
    commBtn.current = null;
  };

  const cancelDraw = () => {
    drawModeRef.current = null;
    setDrawMode(null);
    setDrawVertexCount(0);
    drawStateRef.current = { center: null, vertices: [] };
    if (map.current) {
      map.current.getCanvas().style.cursor = '';
      map.current
        .getSource('drawPreview')
        ?.setData({ type: 'FeatureCollection', features: [] });
    }
  };

  const confirmShape = shape => {
    if (!shape) return;
    const allFeatures = Object.values(commGeoRef.current).flatMap(
      g => g.features
    );
    if (!allFeatures.length) {
      cancelDraw();
      return;
    }

    const matching = allFeatures.filter(f =>
      turf.booleanPointInPolygon(turf.centroid(f), shape)
    );

    if (matching.length === 0) {
      message.info('Aucune commune dans cette zone');
      cancelDraw();
      return;
    }

    const newComm = matching.map(f => {
      const id = String(f.properties.code);
      const nom = f.properties.nom || '';
      const codesPostaux = f.properties.codesPostaux || [];
      const cp = codesPostaux[0] || '';
      const [lng, lat] = turf.centroid(f).geometry.coordinates;
      return { id, nom, cp, codesPostaux, lng, lat };
    });

    // Remplace la sélection des depts en mode communes par les communes dans la zone
    const activeDepts = new Set(Object.keys(commGeoRef.current));
    setUserComm(prev => [
      ...prev.filter(o => !activeDepts.has(id2dept(o.id))),
      ...newComm,
    ]);

    message.success(
      `${matching.length} commune${matching.length > 1 ? 's' : ''} sélectionnée${matching.length > 1 ? 's' : ''}`
    );
    cancelDraw();
  };

  function showCommPopup(feature, exclude = false) {
    if (!communesRef.current) return;
    const [lng, lat] = turf.centroid(feature).geometry.coordinates;
    const html = exclude
      ? `<div class="caddie-label" style="background:#fde8e8;border-color:#c0392b;color:#7f1d1d">✕ Exclure ${feature.properties.nom}</div>`
      : `<div class="caddie-label">🛒 ${feature.properties.nom}</div>`;
    popupRef.current.setLngLat([lng, lat]).setHTML(html).addTo(map.current);
  }

  function hideCommPopup() {
    popupRef.current?.remove();
  }

  const loadRegion = async code => {
    communesRef.current = false;
    if (clickRef.current) {
      map.current.off('click', 'geo-fill', clickRef.current);
      map.current.off('click', 'comm-selected-fill', clickRef.current);
      clickRef.current = null;
    }
    if (hoverMoveRef.current) {
      map.current.off('mousemove', 'geo-fill', hoverMoveRef.current);
      map.current.off('mouseleave', 'geo-fill', hoverLeaveRef.current);
      hoverMoveRef.current = null;
      hoverLeaveRef.current = null;
    }
    hideCommPopup();

    setLoading(true);

    clearCommBtn();
    Object.values(commMarkers.current).forEach(m => m.remove());
    commMarkers.current = {};

    activeComm.current = null;

    const regGeo = regionsGeo || (await fetch(REGIONS_GEO).then(r => r.json()));
    if (!regionsGeo) setRegionsGeo(regGeo);

    const depGeo = deptsGeo || (await fetch(DEPTS_GEO).then(r => r.json()));
    if (!deptsGeo) setDeptsGeo(depGeo);

    const reg = regGeo.features.find(f => f.properties.code === code);
    const depCodes = await fetch(
      `https://geo.api.gouv.fr/regions/${code}/departements?fields=code`
    )
      .then(r => r.json())
      .then(a => a.map(o => o.code));
    const deps = depGeo.features.filter(f =>
      depCodes.includes(f.properties.code)
    );

    map.current.getSource('geo').setData({
      type: 'FeatureCollection',
      features: [reg],
    });
    map.current.getSource('dept').setData({
      type: 'FeatureCollection',
      features: deps,
    });
    map.current.setPaintProperty('geo-fill', 'fill-color', REGION_FILL);
    map.current.setPaintProperty('geo-fill', 'fill-opacity', 0.3);
    map.current.setPaintProperty('geo-line', 'line-opacity', 1);
    map.current.setLayoutProperty('dept-line', 'visibility', 'visible');

    deps.forEach(addDeptMarker);
    map.current.fitBounds(turf.bbox(reg), { padding: 20 });
    setLoading(false);
  };

  const loadDepartement = async (code, communes) => {
    setLoading(true);
    communesRef.current = communes;

    if (clickRef.current) {
      map.current.off('click', 'geo-fill', clickRef.current);
      map.current.off('click', 'comm-selected-fill', clickRef.current);
      clickRef.current = null;
    }
    if (hoverMoveRef.current) {
      map.current.off('mousemove', 'geo-fill', hoverMoveRef.current);
      map.current.off('mouseleave', 'geo-fill', hoverLeaveRef.current);
      hoverMoveRef.current = null;
      hoverLeaveRef.current = null;
    }
    hideCommPopup();

    clearCommBtn();

    Object.keys(commMarkers.current).forEach(id => {
      if (id2dept(id) === code) {
        removeFixedCommMarker(id);
      }
    });
    activeComm.current = null;

    const depGeo = deptsGeo || (await fetch(DEPTS_GEO).then(r => r.json()));
    if (!deptsGeo) setDeptsGeo(depGeo);
    const dep = depGeo.features.find(f => f.properties.code === code);

    map.current.getSource('dept').setData({
      type: 'FeatureCollection',
      features: [dep],
    });
    map.current.setLayoutProperty(
      'dept-line',
      'visibility',
      communes ? 'none' : 'visible'
    );

    if (!communes) {
      // Ne pas écraser la grille de communes si d'autres depts sont en mode communes
      if (Object.keys(commGeoRef.current).length === 0) {
        map.current.getSource('geo').setData({
          type: 'FeatureCollection',
          features: [dep],
        });
        map.current.setPaintProperty('geo-fill', 'fill-color', DEPT_FILL);
        map.current.setPaintProperty('geo-fill', 'fill-opacity', 0.3);
        map.current.setPaintProperty('geo-line', 'line-opacity', 0);
      }
      map.current.setFilter('comm-selected', [
        'in',
        ['get', 'code'],
        ['literal', []],
      ]);

      addDeptMarker(dep);
      userDepts.forEach(codeSel => {
        if (codeSel !== code && !deptMarkers.current[codeSel]) {
          const f = depGeo.features.find(x => x.properties.code === codeSel);
          if (f) addDeptMarker(f);
        }
      });
      map.current.fitBounds(turf.bbox(dep), { padding: 20 });
      userComm.forEach(c => {
        if (id2dept(c.id) === code && !commMarkers.current[c.id]) {
          addFixedCommMarker(c);
        }
      });
      setLoading(false);
      return;
    }

    const geo = await fetch(
      `https://cadastre.data.gouv.fr/bundler/cadastre-etalab/departements/${code}/geojson/communes`
    ).then(r => r.json());
    const cpMap = await fetch(
      `https://geo.api.gouv.fr/departements/${code}/communes?fields=code,codesPostaux`
    )
      .then(r => r.json())
      .then(arr =>
        Object.fromEntries(
          arr.map(o => [
            o.code,
            Array.isArray(o.codesPostaux) ? o.codesPostaux : [o.codesPostaux],
          ])
        )
      );
    geo.features.forEach(f => {
      const insee = f.properties.code || f.id;
      f.properties.codesPostaux = cpMap[insee] || [];
      f.properties.code = String(insee);
    });

    // Stocker le GeoJSON communes pour les outils de dessin
    commGeoRef.current[code] = geo;
    commDeptFeaturesRef.current[code] = dep;
    refreshCommDeptOutlines();

    // Merger les communes de tous les depts en mode communes
    const mergedGeo = {
      type: 'FeatureCollection',
      features: Object.values(commGeoRef.current).flatMap(g => g.features),
    };
    map.current.getSource('geo').setData(mergedGeo);
    communesRef.current = true;
    map.current.setPaintProperty('geo-fill', 'fill-opacity', 0.01);
    map.current.setPaintProperty('geo-line', 'line-opacity', 1);

    if (!communesRef.current) {
      setLoading(false);
      return;
    }

    hoverMoveRef.current = e => {
      const f = e.features?.[0];
      if (!f) return;

      const insee = String(f.properties.code);
      const isSelected = userCommRef.current.some(o => o.id === insee);

      map.current.setFilter('comm-hover', ['==', ['get', 'code'], insee]);
      showCommPopup(f, isSelected);
    };

    hoverLeaveRef.current = () => {
      map.current.setFilter('comm-hover', ['==', ['get', 'code'], '']);
      hideCommPopup();
    };

    map.current.on('mousemove', 'geo-fill', hoverMoveRef.current);
    map.current.on('mouseleave', 'geo-fill', hoverLeaveRef.current);

    clickRef.current = handleGeoClick;
    map.current.on('click', 'geo-fill', clickRef.current);
    map.current.on('click', 'comm-selected-fill', clickRef.current);

    map.current.fitBounds(turf.bbox(geo), { padding: 20 });
    userDepts.forEach(codeSel => {
      if (!deptMarkers.current[codeSel]) {
        const feat = deptsGeo.features.find(f => f.properties.code === codeSel);
        if (feat) addDeptMarker(feat);
      }
    });

    setLoading(false);
  };

  useEffect(() => {
    if (mapLoaded && selRegion) loadRegion(selRegion);
  }, [mapLoaded, selRegion]);
  useEffect(() => {
    if (mapLoaded && selDept && !viewMode) loadDepartement(selDept, commMode);
  }, [mapLoaded, selDept, commMode, viewMode]);

  useEffect(() => {
    if (!mapLoaded) return;

    const updateSelectedDepts = async () => {
      let allDeps = deptsGeo;
      if (!allDeps) {
        allDeps = await fetch(DEPTS_GEO).then(r => r.json());
        setDeptsGeo(allDeps);
      }

      const features = allDeps.features.filter(f =>
        userDepts.includes(f.properties.code)
      );

      features.forEach(f => {
        const code = f.properties.code;
        if (!deptMarkers.current[code]) addDeptMarker(f);
      });
      map.current.getSource('deptSel').setData({
        type: 'FeatureCollection',
        features,
      });
    };

    updateSelectedDepts();
  }, [userDepts, deptsGeo, mapLoaded]);

  useEffect(() => {
    if (commMode && selDept) {
      setUserDepts(prev => prev.filter(code => code !== selDept));
    }
  }, [commMode, selDept]);

  useEffect(() => {
    if (mapLoaded && !commMode) {
      clearCommBtn();
      activeComm.current = null;
      hideCommPopup();

      map.current.setFilter('comm-hover', ['==', ['get', 'code'], '']);

      map.current.off('mousemove', 'geo-fill');
      map.current.off('mouseleave', 'geo-fill');
      map.current.off('click', 'geo-fill');
      map.current.off('click', 'comm-selected-fill');
      popupRef.current?.remove();
    }
  }, [commMode, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !deptsGeo) return;

    const features = deptsGeo.features.filter(f =>
      userDepts.includes(f.properties.code)
    );

    map.current.getSource('deptSel').setData({
      type: 'FeatureCollection',
      features,
    });
  }, [userDepts, deptsGeo, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded) return;

    const buildCommFeatures = async () => {
      const groups = {};
      userComm.forEach(({ id }) => {
        const dep = id2dept(id);
        (groups[dep] ||= []).push(id);
      });

      const coll = { type: 'FeatureCollection', features: [] };

      for (const dep of Object.keys(groups)) {
        let geoDep = null;
        if (commGeoRef.current[dep]) {
          geoDep = commGeoRef.current[dep];
        } else {
          try {
            geoDep = await fetch(
              `https://cadastre.data.gouv.fr/bundler/cadastre-etalab/departements/${dep}/geojson/communes`
            ).then(r => r.json());
            if (geoDep?.features) commGeoRef.current[dep] = geoDep;
          } catch (e) {
            console.warn(
              `buildCommFeatures: impossible de charger le dept ${dep}`,
              e
            );
          }
        }

        if (!geoDep?.features) continue;

        geoDep.features.forEach(f => {
          if (groups[dep].includes(String(f.properties.code || f.id))) {
            coll.features.push(f);
          }
        });
      }

      map.current.getSource('commSel').setData(coll);
      userComm.forEach(c => {
        if (!commMarkers.current[c.id]) addFixedCommMarker(c);
      });
    };

    buildCommFeatures();
  }, [userComm, selDept, mapLoaded]);

  // Affiche la zone géographique sur la source viewZone quand une source Visualiser est sélectionnée
  useEffect(() => {
    if (!mapLoaded) return;

    const empty = { type: 'FeatureCollection', features: [] };

    if (!viewSource) {
      map.current.getSource('viewZone').setData(empty);
      map.current.getSource('geo').setData(empty);
      // commSel est géré par l'effet userComm — ne pas l'écraser ici
      return;
    }

    let depts = [];
    let inseeList = [];
    let cpList = [];

    if (viewSource.type === 'playlist') {
      const playlist = viewPlaylists.find(p => p.id === viewSource.id);
      if (!playlist) return;
      // Playlists gestion : pas de zone géo SIRET à afficher
      if (playlist.source_type === 'gestion') return;
      const f =
        typeof playlist.filters === 'string'
          ? JSON.parse(playlist.filters)
          : playlist.filters || {};
      // Normalise : nombre (44), string ("44") ou tableau (["44"])
      const raw = f.dept ?? f.depts;
      if (Array.isArray(raw)) {
        depts = raw.map(String);
      } else if (raw !== null && raw !== undefined) {
        depts = [String(raw)];
      }
    } else {
      const product = viewProducts.find(p => p.slug === viewSource.id);
      if (!product) return;
      const fd = product.filter_departments;
      if (Array.isArray(fd)) depts = fd.map(String);
      else if (typeof fd === 'string') {
        try {
          const parsed = JSON.parse(fd);
          depts = Array.isArray(parsed) ? parsed.map(String) : [];
        } catch {
          depts = [];
        }
      }
      // Communes INSEE du produit
      const inseeRaw = product.filter_communes_insee;
      inseeList = Array.isArray(inseeRaw)
        ? inseeRaw.map(String)
        : typeof inseeRaw === 'string'
          ? JSON.parse(inseeRaw || '[]').map(String)
          : [];
      // Codes postaux du produit (même filtre côté backend)
      const cpRaw = product.filter_communes;
      cpList = Array.isArray(cpRaw)
        ? cpRaw.map(String)
        : typeof cpRaw === 'string'
          ? JSON.parse(cpRaw || '[]').map(String)
          : [];
      // Fallback : dériver les depts depuis les codes INSEE ou codes postaux
      if (!depts.length) {
        const fromInsee = inseeList.map(c => c.slice(0, 2));
        const fromCp = cpList.map(c => c.slice(0, 2));
        depts = Array.from(new Set([...fromInsee, ...fromCp]));
      }
    }

    if (!depts.length) return;

    const showZone = async () => {
      let allDeps = deptsGeo;
      if (!allDeps) {
        allDeps = await fetch(DEPTS_GEO).then(r => r.json());
        setDeptsGeo(allDeps);
      }
      const features = allDeps.features.filter(f =>
        depts.map(String).includes(String(f.properties.code))
      );
      if (!features.length) return;
      const geo = { type: 'FeatureCollection', features };
      map.current.getSource('viewZone').setData(geo);
      map.current.fitBounds(turf.bbox(geo), { padding: 60 });

      // Afficher les contours communes si le produit est filtré par communes
      if (inseeList.length || cpList.length) {
        const cpSet = new Set(cpList);
        // Charger GeoJSON communes + cpMap pour chaque dept en parallèle
        const [geoJsons, cpMaps] = await Promise.all([
          Promise.all(
            depts.map(dep =>
              commGeoRef.current[dep]
                ? Promise.resolve(commGeoRef.current[dep])
                : fetch(
                    `https://cadastre.data.gouv.fr/bundler/cadastre-etalab/departements/${dep}/geojson/communes`
                  )
                    .then(r => r.json())
                    .then(g => {
                      commGeoRef.current[dep] = g;
                      return g;
                    })
            )
          ),
          cpList.length
            ? Promise.all(
                depts.map(dep =>
                  fetch(
                    `https://geo.api.gouv.fr/departements/${dep}/communes?fields=code,codesPostaux`
                  )
                    .then(r => r.json())
                    .then(arr =>
                      Object.fromEntries(
                        arr.map(o => [
                          o.code,
                          Array.isArray(o.codesPostaux)
                            ? o.codesPostaux
                            : [o.codesPostaux],
                        ])
                      )
                    )
                )
              )
            : Promise.resolve([]),
        ]);

        // Si des codes INSEE explicites existent, les utiliser tels quels (pas d'expansion CP).
        // Sinon, expansion codes postaux → INSEE (pour anciens produits sans filter_communes_insee).
        const inseeSet = new Set(inseeList);
        if (inseeList.length === 0) {
          cpMaps.forEach(cpMap => {
            Object.entries(cpMap).forEach(([insee, cps]) => {
              if (cps.some(cp => cpSet.has(String(cp)))) inseeSet.add(insee);
            });
          });
        }

        const allFeatures = geoJsons.flatMap(g => g.features || []);
        const allCommunes = {
          type: 'FeatureCollection',
          features: allFeatures,
        };
        const selCommunes = {
          type: 'FeatureCollection',
          features: allFeatures.filter(f =>
            inseeSet.has(String(f.properties.code || f.id))
          ),
        };
        map.current.getSource('geo').setData(allCommunes);
        map.current.setPaintProperty('geo-fill', 'fill-opacity', 0.01);
        map.current.setPaintProperty('geo-line', 'line-opacity', 0.5);
        map.current.getSource('commSel').setData(selCommunes);
      } else {
        map.current.getSource('geo').setData(empty);
        map.current.getSource('commSel').setData(empty);
      }
    };

    showZone();
  }, [viewSource, mapLoaded, viewProducts, viewPlaylists]);

  useEffect(() => {
    if (!mapLoaded || !drawMode) return;

    const handleClick = e => {
      const { lng, lat } = e.lngLat;
      if (drawModeRef.current === 'circle') {
        if (!drawStateRef.current.center) {
          drawStateRef.current.center = [lng, lat];
        } else {
          confirmShape(drawStateRef.current.shape);
        }
      } else if (drawModeRef.current === 'polygon') {
        drawStateRef.current.vertices.push([lng, lat]);
        setDrawVertexCount(drawStateRef.current.vertices.length);
        const verts = drawStateRef.current.vertices;
        if (verts.length >= 2) {
          const preview = turf.lineString([...verts, verts[0]]);
          map.current
            .getSource('drawPreview')
            .setData({ type: 'FeatureCollection', features: [preview] });
        }
      }
    };

    const handleMove = e => {
      const { lng, lat } = e.lngLat;
      if (drawModeRef.current === 'circle' && drawStateRef.current.center) {
        const center = drawStateRef.current.center;
        const radius = turf.distance(center, [lng, lat], {
          units: 'kilometers',
        });
        const circle = turf.circle(center, Math.max(radius, 0.01), {
          steps: 64,
        });
        drawStateRef.current.shape = circle;
        map.current
          .getSource('drawPreview')
          .setData({ type: 'FeatureCollection', features: [circle] });
      } else if (
        drawModeRef.current === 'polygon' &&
        drawStateRef.current.vertices.length > 0
      ) {
        const verts = [...drawStateRef.current.vertices, [lng, lat]];
        const preview =
          verts.length >= 3
            ? turf.polygon([[...verts, verts[0]]])
            : turf.lineString([...verts, verts[0]]);
        map.current
          .getSource('drawPreview')
          .setData({ type: 'FeatureCollection', features: [preview] });
      }
    };

    map.current.on('click', handleClick);
    map.current.on('mousemove', handleMove);

    return () => {
      map.current.off('click', handleClick);
      map.current.off('mousemove', handleMove);
    };
  }, [mapLoaded, drawMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateSubmit = async ({ name }) => {
    const filters = buildFiltersPayload(); // 👈 même payload que pour le count

    let expectedCount = 0;
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL}/targets/count`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(filters),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      expectedCount = data.count || 0;
    } catch (e) {
      message.error(`Impossible d'évaluer le nombre de cibles : ${e.message}`);
      return;
    }

    try {
      const body = {
        name,
        userId: user?.id,
        ...buildFiltersPayload(),
        expectedCount,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/playlists/from-selection`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      message.success(`Playlist créée (ID ${json.id}) — ${json.tracks} cibles`);
    } catch (err) {
      message.error(err.message);
    }
  };

  const activitySummary = useMemo(
    () => buildActivitySummary(selNaf, macroThemes),
    [selNaf, macroThemes]
  );

  const viewTitle = useMemo(() => {
    if (!viewSource) return '';
    if (viewSource.type === 'playlist') {
      return (
        viewPlaylists.find(p => p.id === viewSource.id)?.name || viewSource.id
      );
    }
    return (
      viewProducts.find(p => p.slug === viewSource.id)?.name || viewSource.id
    );
  }, [viewSource, viewPlaylists, viewProducts]);

  return (
    <>
      {webGLError ? (
        <div
          style={{
            position: 'fixed',
            top: HEADER_H,
            left: 0,
            bottom: 0,
            right: drawerOpen ? drawerWidth : 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f5f5f5',
            zIndex: 0,
          }}
        >
          <Alert
            type="error"
            showIcon
            message="La carte ne peut pas s'afficher"
            description="Votre navigateur ne supporte pas WebGL. Essayez d'activer l'accélération matérielle dans les paramètres de Chrome (Paramètres → Système → Utiliser l'accélération graphique)."
            style={{ maxWidth: 480 }}
          />
        </div>
      ) : (
        <div
          ref={container}
          style={{
            position: 'fixed',
            top: HEADER_H,
            left: 0,
            bottom: 0,
            right: drawerOpen ? drawerWidth : 0,
            transition: 'right 0.3s',
            zIndex: 0,
          }}
        />
      )}
      <style>{PLAIN_POPUP_CSS}</style>

      <Drawer
        placement="right"
        width={drawerWidth}
        mask={false}
        getContainer={false}
        open={drawerOpen}
        closable={false}
        rootStyle={{
          position: 'fixed',
          top: HEADER_H,
          right: 0,
          height: `calc(100vh - ${HEADER_H}px)`,
          zIndex: 1100,
        }}
        styles={{
          content: { height: '100%' },
          body: { height: '100%', overflow: 'auto', paddingBottom: 12 },
          header: { borderBottom: '1px solid #f0f0f0' },
        }}
        title={viewMode ? null : 'Filtres et sélections'}
        extra={
          viewMode ? null : (
            <div style={{ display: 'flex', gap: 8 }}>
              <Tooltip
                placement="bottomRight"
                title={
                  updateProduct
                    ? `Mettre à jour : ${updateProduct.name}`
                    : 'Sauvegarder comme produit packagé'
                }
              >
                <Button
                  type="default"
                  onClick={() => setSaveProductOpen(true)}
                  style={{
                    background: updateProduct ? '#f0fff4' : '#f0f5ff',
                    borderColor: updateProduct ? '#52c41a' : '#adc6ff',
                    color: updateProduct ? '#237804' : '#1d39c4',
                  }}
                >
                  {updateProduct ? 'Mettre à jour' : 'Sauvegarder produit'}
                </Button>
              </Tooltip>
              {updateProduct && (
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    const slug = updateProduct.slug;
                    setUpdateProduct(null);
                    clearCommMarkers();
                    setUserComm([]);
                    setUserDepts([]);
                    setSelDept(null);
                    setCommMode(false);
                    setSelNaf([]);
                    setViewSource({ type: 'product', id: slug });
                    setViewMode(true);
                    setViewListOpen(true);
                    autoLoadRef.current = true;
                  }}
                  style={{
                    borderColor: '#ffa39e',
                    color: '#cf1322',
                    background: '#fff1f0',
                    fontSize: 12,
                  }}
                >
                  Quitter l'édition
                </Button>
              )}
            </div>
          )
        }
      >
        {viewMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{
                alignSelf: 'flex-start',
                paddingLeft: 0,
                color: '#475569',
              }}
            >
              Retour
            </Button>

            <div>
              {(() => {
                const isGestionView =
                  viewSource?.type === 'playlist' &&
                  viewPlaylists.find(p => p.id === viewSource.id)
                    ?.source_type === 'gestion';
                const tagColor = isGestionView
                  ? 'orange'
                  : viewSource?.type === 'playlist'
                    ? 'purple'
                    : 'blue';
                const tagLabel = isGestionView
                  ? 'Playlist Gestion'
                  : viewSource?.type === 'playlist'
                    ? 'Playlist'
                    : 'Produit packagé';
                return (
                  <Tag color={tagColor} style={{ marginBottom: 8 }}>
                    {tagLabel}
                  </Tag>
                );
              })()}
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 17,
                  color: '#1e293b',
                  lineHeight: 1.3,
                }}
              >
                {viewTitle}
              </div>
            </div>

            {viewSource?.type === 'product' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button
                  icon={<GlobalOutlined />}
                  href={`https://devis-localise.com/produits/${viewSource.id}`}
                  target="_blank"
                  block
                >
                  Voir sur le site
                </Button>
                <Button
                  icon={<EditOutlined />}
                  onClick={handleEditProduct}
                  block
                >
                  Modifier la sélection
                </Button>
              </div>
            )}

            {viewTotals.totalAll > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Statistic
                  value={viewTotals.totalAll}
                  suffix="contacts au total"
                  valueStyle={{ fontSize: 18 }}
                />
                <Statistic
                  value={viewTotals.totalGeo}
                  suffix="géolocalisés (affichables sur carte)"
                  valueStyle={{ fontSize: 15, color: '#3b82f6' }}
                />
                {establishments.length > 0 && (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {establishments.length} affichés sur la carte
                  </div>
                )}
              </div>
            )}

            <Divider style={{ margin: '0' }} />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#f8fafc',
                borderRadius: 8,
                padding: '8px 12px',
              }}
            >
              <span style={{ fontSize: 12, color: '#475569' }}>
                Page suivante : ajouter aux existants
              </span>
              <Switch
                size="small"
                checked={viewAppend}
                onChange={setViewAppend}
              />
            </div>

            {viewPage > 0 && establishments.length >= 300 && (
              <Button
                type="primary"
                loading={viewLoading}
                onClick={() => handleViewOnMap(viewPage + 1)}
                block
              >
                Page {viewPage + 1} →
              </Button>
            )}

            {establishments.length > 0 && (
              <Button
                danger
                onClick={() => {
                  setEstablishments([]);
                  setViewPage(0);
                  setSelectedEst(null);
                  estPopupRef.current?.remove();
                }}
                block
              >
                Effacer la carte
              </Button>
            )}
          </div>
        ) : (
          <>
            <Tabs
              size="small"
              tabBarGutter={8}
              activeKey={activeFilterTab}
              onChange={setActiveFilterTab}
              items={[
                {
                  key: 'geo',
                  label: 'Géographie',
                  children: (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                      }}
                    >
                      <Select
                        placeholder="Région"
                        showSearch
                        value={selRegion}
                        allowClear
                        onChange={v => {
                          setSelRegion(v);
                          setSelDept(null);
                          setCommMode(false);
                        }}
                        options={regionList.map(r => ({
                          value: r.code,
                          label: `${r.code} – ${r.nom}`,
                        }))}
                      />

                      <Select
                        placeholder="Département"
                        showSearch
                        value={selDept}
                        allowClear
                        onChange={v => {
                          setSelDept(v);
                          setSelRegion(null);
                          setCommMode(false);
                        }}
                        options={deptList.map(d => ({
                          value: d.code,
                          label: `${d.code} – ${d.nom}`,
                        }))}
                      />

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <Switch
                          checked={commMode}
                          onChange={v => {
                            cancelDraw();
                            if (!v && selDept) {
                              delete commGeoRef.current[selDept];
                              delete commDeptFeaturesRef.current[selDept];
                              refreshCommDeptOutlines();
                              const remaining = Object.values(
                                commGeoRef.current
                              );
                              if (remaining.length > 0) {
                                map.current.getSource('geo').setData({
                                  type: 'FeatureCollection',
                                  features: remaining.flatMap(g => g.features),
                                });
                              } else {
                                communesRef.current = false;
                              }
                              Object.keys(commMarkers.current)
                                .filter(id => id2dept(id) === selDept)
                                .forEach(id => removeFixedCommMarker(id));
                              setUserComm(prev =>
                                prev.filter(o => id2dept(o.id) !== selDept)
                              );
                            }
                            setCommMode(v);
                          }}
                          disabled={!selDept}
                        />
                        <Text>Filtrer par communes</Text>
                        {loadingDrawer && <Spin size="small" />}
                      </div>

                      {userComm.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <Switch
                            size="small"
                            checked={showCommLabels}
                            onChange={setShowCommLabels}
                          />
                          <Text style={{ fontSize: 12 }}>
                            Afficher les noms
                          </Text>
                        </div>
                      )}

                      {commMode && !drawMode && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Button
                            size="small"
                            icon={<span>⬤</span>}
                            onClick={() => {
                              drawModeRef.current = 'circle';
                              setDrawMode('circle');
                              drawStateRef.current = {
                                center: null,
                                vertices: [],
                              };
                              map.current.getCanvas().style.cursor =
                                'crosshair';
                            }}
                          >
                            Cercle
                          </Button>
                          <Button
                            size="small"
                            icon={<span>⬡</span>}
                            onClick={() => {
                              drawModeRef.current = 'polygon';
                              setDrawMode('polygon');
                              setDrawVertexCount(0);
                              drawStateRef.current = {
                                center: null,
                                vertices: [],
                              };
                              map.current.getCanvas().style.cursor =
                                'crosshair';
                            }}
                          >
                            Polygone
                          </Button>
                        </div>
                      )}

                      {drawMode && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            background: '#eff6ff',
                            borderRadius: 6,
                            border: '1px solid #bfdbfe',
                          }}
                        >
                          <span
                            style={{ fontSize: 12, color: '#1d4ed8', flex: 1 }}
                          >
                            {drawMode === 'circle'
                              ? drawStateRef.current.center
                                ? 'Cliquez pour valider le cercle'
                                : 'Cliquez pour définir le centre'
                              : drawVertexCount < 3
                                ? `${drawVertexCount} point${drawVertexCount > 1 ? 's' : ''} — cliquez pour ajouter des sommets`
                                : `${drawVertexCount} points`}
                          </span>
                          {drawMode === 'polygon' && drawVertexCount >= 3 && (
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => {
                                const verts = drawStateRef.current.vertices;
                                if (verts.length >= 3) {
                                  confirmShape(
                                    turf.polygon([[...verts, verts[0]]])
                                  );
                                }
                              }}
                            >
                              Valider
                            </Button>
                          )}
                          <Button size="small" danger onClick={cancelDraw}>
                            Annuler
                          </Button>
                        </div>
                      )}

                      <div style={{ marginTop: 8 }}>
                        <span>Avec e-mail uniquement&nbsp;</span>
                        <Switch
                          checked={emailOnly}
                          onChange={checked => setEmailOnly(checked)}
                        />
                        {!emailOnly && (
                          <span style={{ fontSize: 12, color: '#d46b08' }}>
                            requis pour créer une playlist
                          </span>
                        )}
                      </div>

                      {count !== null && (
                        <>
                          <Statistic
                            value={count}
                            suffix="établissements"
                            loading={countLoading}
                            valueStyle={{ fontSize: 18 }}
                          />
                          <Button
                            type="primary"
                            onClick={() => {
                              setShowEstablishments(true);
                              loadEstablishments(1);
                            }}
                            loading={establishmentsLoading}
                            disabled={count === 0}
                            style={{ marginTop: 8, width: '100%' }}
                          >
                            Afficher sur la carte
                          </Button>
                          {establishments.length > 0 && (
                            <div
                              style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: '#666',
                              }}
                            >
                              {establishments.length} établissement(s)
                              affiché(s)
                              {establishments.length >= 300 && (
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() =>
                                    loadEstablishments(currentPage + 1)
                                  }
                                  loading={establishmentsLoading}
                                >
                                  Charger plus (page {currentPage + 1})
                                </Button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'activity',
                  label: 'Activité',
                  children: (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100vh - 260px)',
                      }}
                    >
                      <NafPicker value={selNaf} onChange={setSelNaf} />
                    </div>
                  ),
                },
              ]}
            />

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ display: 'grid', gap: 12 }}>
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #eee',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <Text strong>Géographie</Text>
                  <Button
                    size="small"
                    type="text"
                    onClick={() => {
                      setUserDepts([]);
                      clearCommMarkers();
                      setUserComm([]);
                    }}
                  >
                    Effacer
                  </Button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {userDepts.map(code => (
                    <Tag
                      key={`dep-${code}`}
                      color="volcano"
                      closable
                      onClose={() =>
                        setUserDepts(p => p.filter(x => x !== code))
                      }
                    >
                      {deptList.find(d => d.code === code)?.nom || code} ({code}
                      )
                    </Tag>
                  ))}
                  {userComm.map(({ id, nom, cp }) => (
                    <Tag
                      key={`${id}-${cp}`}
                      color="blue"
                      closable
                      onClose={() =>
                        setUserComm(p =>
                          p.filter(o => !(o.id === id && o.cp === cp))
                        )
                      }
                    >
                      {nom} — {cp}
                    </Tag>
                  ))}
                  {!userDepts.length && !userComm.length && (
                    <Text type="secondary">Aucune sélection géographique</Text>
                  )}
                </div>
              </div>

              <div
                style={{
                  background: '#fff',
                  border: '1px solid #eee',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <Text strong>Activité</Text>
                  <Button
                    size="small"
                    type="text"
                    onClick={() => setSelNaf([])}
                    disabled={!selNaf.length}
                  >
                    Effacer
                  </Button>
                </div>

                {activitySummary.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {activitySummary.map(item => (
                      <Tag
                        key={item.key}
                        closable
                        onClose={() => {
                          if (item.type === 'subtheme') {
                            setSelNaf(prev =>
                              prev.filter(c => !item.codes.includes(c))
                            );
                          } else {
                            setSelNaf(prev =>
                              prev.filter(c => c !== item.codes[0])
                            );
                          }
                        }}
                      >
                        {item.label}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary">Aucune activité sélectionnée</Text>
                )}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 12,
              }}
            >
              {!updateProduct && (
                <Button
                  onClick={() => {
                    setSelRegion(null);
                    setSelDept(null);
                    setCommMode(false);
                    setUserDepts([]);
                    clearCommMarkers();
                    setUserComm([]);
                    setSelNaf([]);
                    commGeoRef.current = {};
                    commDeptFeaturesRef.current = {};
                    refreshCommDeptOutlines();
                  }}
                >
                  Réinitialiser
                </Button>
              )}
              {count !== null && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Statistic
                    value={count}
                    suffix="établissements"
                    valueStyle={{ fontSize: 16 }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </Drawer>

      <CreatePlaylistDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateSubmit}
        depts={userDepts}
        deptList={deptList}
        communes={userComm}
        activityItems={activitySummary}
        emailOnly={emailOnly}
        onEdit={tabKey => {
          setCreateOpen(false);
          setActiveFilterTab(tabKey);
        }}
      />

      <SaveProductDrawer
        open={saveProductOpen}
        onClose={() => {
          setSaveProductOpen(false);
          if (updateProduct) {
            // Nettoyer l'état carte après édition produit
            clearCommMarkers();
            setUserComm([]);
            setUserDepts([]);
            setSelDept(null);
            setCommMode(false);
            setSelNaf([]);
          }
          setUpdateProduct(null);
        }}
        onSaved={slug => {
          (async () => {
            await loadViewLists(true);
            setViewMode(true);
            setViewSource({ type: 'product', id: slug });
            setViewListOpen(true);
            autoLoadRef.current = true;
          })();
        }}
        filters={buildFiltersPayload()}
        targetCount={count}
        activitySummary={activitySummary}
        updateProduct={updateProduct}
      />

      {/* Drawer liste établissements style Airbnb */}
      <Drawer
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>
              {establishments.length} établissement
              {establishments.length > 1 ? 's' : ''} affichés
            </span>
            {viewPage > 0 && establishments.length >= 300 && (
              <Button
                size="small"
                type="primary"
                loading={viewLoading}
                onClick={() => handleViewOnMap(viewPage + 1)}
              >
                Page {viewPage + 1} →
              </Button>
            )}
            {viewPage === 0 && establishments.length >= 300 && (
              <Button
                size="small"
                type="primary"
                loading={establishmentsLoading}
                onClick={() => loadEstablishments(currentPage + 1)}
              >
                Page {currentPage + 1} →
              </Button>
            )}
          </div>
        }
        placement="left"
        width={360}
        mask={false}
        getContainer={false}
        open={viewListOpen}
        onClose={() => setViewListOpen(false)}
        rootStyle={{
          zIndex: 1200,
          position: 'fixed',
          top: HEADER_H,
          height: `calc(100vh - ${HEADER_H}px)`,
        }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {establishments.map((est, i) => (
            <div
              key={`${est.name}-${i}`}
              onClick={() => {
                if (!map.current || !est.longitude || !est.latitude) return;
                setSelectedEst(i);
                map.current.flyTo({
                  center: [est.longitude, est.latitude],
                  zoom: 14,
                  duration: 800,
                });
                estPopupRef.current?.remove();
                estPopupRef.current = new mapboxgl.Popup({
                  closeButton: true,
                  maxWidth: '240px',
                })
                  .setLngLat([est.longitude, est.latitude])
                  .setHTML(
                    `<div style="font-family:sans-serif;font-size:13px;line-height:1.4">
                      <div style="font-weight:700;margin-bottom:4px">${est.name}</div>
                      <div style="color:#555;font-size:12px">${est.address}</div>
                      <div style="color:#555;font-size:12px">${est.postalCode} ${est.city}</div>
                      <div style="margin-top:6px;font-size:11px;color:#888">${est.activity}</div>
                    </div>`
                  )
                  .addTo(map.current);
              }}
              style={{
                background: selectedEst === i ? '#eff6ff' : '#fff',
                border:
                  selectedEst === i
                    ? '1.5px solid #3b82f6'
                    : '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 14px',
                boxShadow:
                  selectedEst === i
                    ? '0 0 0 3px rgba(59,130,246,0.15)'
                    : '0 1px 3px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: '#1e293b',
                  marginBottom: 2,
                  lineHeight: 1.3,
                }}
              >
                {est.name}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                {est.city}
                {est.postalCode ? ` — ${est.postalCode}` : ''}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: 4,
                    padding: '1px 6px',
                  }}
                >
                  {est.activity}
                </span>
              </div>
            </div>
          ))}
          {viewPage > 0 && establishments.length >= 300 && (
            <Button
              type="primary"
              loading={viewLoading}
              onClick={() => handleViewOnMap(viewPage + 1)}
              block
              style={{ marginTop: 8 }}
            >
              Charger la page {viewPage + 1} →
            </Button>
          )}
        </div>
      </Drawer>
    </>
  );
}
